import {Deferred} from '@Libraries/deferred';
import {getCacheBustedURL} from '@Utils/url.util';
import type {EmbeddedEditorCallbackLegacy, EmbeddedEditorCallback} from '@Libraries/embedded-editor';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {noop} from '@Utils/general.util';
import type {PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';

const PREVIEW_POLLING_TIMEOUT = 20000;
const LONG_PREVIEW_POLLING_TIMEOUT = 450000;

export interface PosterPreviewPollingResponse {
  isAvailable: boolean;
  data: boolean;
}

export interface PosterPageHashedIdInfo {
  posterHashedId: string;
  pageHashedId: string;
}

export interface PosterPageData {
  posterId: string;
  pageId: string;
  previewUrl: string;
  posterName: string;
  isVideo: boolean;
}

export interface MultipagePreviewPollingResponse {
  isAvailable: boolean;
  data?: {
    previewUrl: string;
  };
}

export const shouldShowRepublishButton = (): boolean => {
  return (window.posterEditor?.config?.hasPublishedUsingDigitalDisplay &&
    window.posterEditor?.whiteboard?.ageOfGeneratedOrder &&
    window.posterEditor?.whiteboard?.ageOfGeneratedOrder >= 0) as boolean;
};

export const loadDesignSelector = (
  designsLoadedCallback: VoidFunction,
  onDesignsSelected: EmbeddedEditorCallback,
  divToLoadIn: string,
  loadPostersIfNotLoadedCustomEvent: string,
  startDesignFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts,
  preselectedDesigns: DesignSelection[] = [],
  scrollContainerEl?: HTMLElement | null
): void => {
  window.PMW.util.require(window.PMW.loadDesignSelectorElement === undefined, 'designSelector', true, () => {
    window.PMW.loadDesignSelectorElement(
      designsLoadedCallback,
      onDesignsSelected,
      divToLoadIn,
      loadPostersIfNotLoadedCustomEvent,
      startDesignFromMediaModalBtnOpts,
      undefined,
      undefined,
      preselectedDesigns,
      undefined,
      scrollContainerEl
    );
  });
};

export const loadDesignSelectorLegacy = (
  designsLoadedCallback: () => void,
  onDesignSelected: EmbeddedEditorCallbackLegacy,
  divToLoadIn: string,
  loadPostersIfNotLoadedCustomEvent: string,
  startDesignFromMediaModalBtnOpts?: CreateDesignFromMediaModalOpts
): void => {
  window.PMW.util.require(window.PMW.loadDesignSelectorElementLegacy === undefined, 'designSelector', true, () => {
    window.PMW.loadDesignSelectorElementLegacy(designsLoadedCallback, onDesignSelected, divToLoadIn, loadPostersIfNotLoadedCustomEvent, startDesignFromMediaModalBtnOpts);
  });
};

export const loadDesignGallery = async (
  contentElementSelector: string,
  contentContainerSelector: string,
  disableCreateFromScratchCTA: boolean,
  startingPageURI: GalleryPageURI = GalleryThemeURI.DEFAULT
): Promise<unknown> => {
  const deferred = new Deferred();

  window.PMW.util.require(window.PMW.initGalleryContentInElement === undefined, 'galleryDialog', false, () => {
    window.PMW.initGalleryContentInElement(true, contentElementSelector, contentContainerSelector, disableCreateFromScratchCTA, startingPageURI);
    deferred.resolve();
  });

  return deferred.promise;
};

export const generatePosterPreview = async (posterHashedId: string): Promise<void> => {
  return (await window.PMW.writeLocal('posterbuilder/generatePreview', {
    posterHashId: posterHashedId,
  })) as Promise<void>;
};

export const generatePosterPreviewWithBeacon = (posterHashedId: string): void => {
  const url = window.PMW.util.site_url('posterbuilder/generatePreview');
  const data = new FormData();
  data.append('posterHashId', posterHashedId);
  navigator.sendBeacon(url, data);
};

const resolvePromiseIfNonPreviewAssetFound = (ajax: XMLHttpRequest, intervalHandler: NodeJS.Timeout, deferred: Deferred): void => {
  const isPlaceholder = parseInt(ajax.getResponseHeader('x-amz-meta-is-placeholder') ?? '0', 10);
  if (!isPlaceholder) {
    clearInterval(intervalHandler);
    deferred.resolve();
  }
};

const resolvePromiseIfUpdatedAssetFound = (ajax: XMLHttpRequest, referenceTime: number, intervalHandler: NodeJS.Timeout, deferred: Deferred): void => {
  if (typeof ajax.getResponseHeader('last-modified') !== 'undefined') {
    const newLastModified = ajax.getResponseHeader('last-modified');
    const isPlaceholder = parseInt(ajax.getResponseHeader('x-amz-meta-is-placeholder') ?? '0', 10);

    if (newLastModified) {
      const lastModifiedTime = Date.parse(newLastModified);

      if (lastModifiedTime > referenceTime && !isPlaceholder) {
        clearInterval(intervalHandler);
        deferred.resolve();
      }
    }
  }
};

const checkForUpdatesInURL = (intervalHandler: NodeJS.Timeout, deferred: Deferred, urlObj: URL, ignoreUpdates: boolean, referenceTime: number): void => {
  const ajax = new XMLHttpRequest();
  ajax.open('HEAD', getCacheBustedURL(urlObj.href), true);
  ajax.onerror = (): void => {
    console.log('error!');
  };

  ajax.onreadystatechange = function (): void {
    if (this.readyState === this.DONE) {
      if (ignoreUpdates) {
        resolvePromiseIfNonPreviewAssetFound(ajax, intervalHandler, deferred);
      } else {
        resolvePromiseIfUpdatedAssetFound(ajax, referenceTime, intervalHandler, deferred);
      }
    }
  };

  ajax.onerror = (): void => {
    clearInterval(intervalHandler);
    deferred.reject();
  };

  ajax.send();
};

export const pollForUpdatesInURL = (url: string, ignoreUpdates = false): Deferred => {
  const referenceTime = new Date().getTime();
  let iteration = 0;
  const MAX_ITERATIONS = 300;
  const ITERATION_TIMEOUT_MS = 2000;
  const deferred = new Deferred();

  if (!url) {
    deferred.reject();
    return deferred;
  }
  const urlObj = new URL(url);

  const intervalHandler = setInterval((): void => {
    if (iteration >= MAX_ITERATIONS) {
      clearInterval(intervalHandler);
      deferred.resolve();
    }

    checkForUpdatesInURL(intervalHandler, deferred, urlObj, ignoreUpdates, referenceTime);
    iteration += 1;
  }, ITERATION_TIMEOUT_MS);

  checkForUpdatesInURL(intervalHandler, deferred, urlObj, ignoreUpdates, referenceTime);

  return deferred;
};

export const getPosterPlaceholderImagePreviewURL = (): string => {
  return window.PMW.util.asset_url('images/landing-page/placeholder-image.png');
};
export const getPosterPlaceholderVideoPreviewURL = (): string => {
  return window.PMW.util.asset_url('images/landing-page/placeholder-video.mp4');
};

// CodeReview: This should be a promise with success as resolve and onError as exception with only one param (posterHashedId), also the current name
// only describes the polling, but the function also triggers generation. Better name would be expediteAndAwaitPosterPreview/generatePosterPreviewAndWait/ensurePosterPreviewGenerated
export const pollForPosterPreviewGenerationStatus = (
  posterHashedId: string,
  onSuccess: (value?: unknown) => void | Promise<void>,
  onError: (err: unknown) => void = noop,
  generateAllPreviews = false,
  timeout: number = PREVIEW_POLLING_TIMEOUT
): void => {
  window.PMW.pollingAjaxCallAsync(
    window.PMW.writeLocal.bind(null, 'posterbuilder/expeditePosterThumbnailGeneration', {
      posterHashId: posterHashedId,
      generateAllPreviews: generateAllPreviews ? '1' : '0',
    }),
    async () => {
      return (await window.PMW.readLocal('posterbuilder/checkPreviewGenerationStatus', {
        posterHashId: posterHashedId,
        generateAllPreviews: generateAllPreviews ? '1' : '0',
      })) as Promise<PosterPreviewPollingResponse>;
    },
    timeout
  ).then(onSuccess, onError);
};

export const generatePosterPreviewForMultipagePoster = (posterHashedId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    window.PMW.pollingAjaxCallAsync(
      window.PMW.writeLocal.bind(null, 'posterbuilder/generateMultipagePosterVideoPreview', {
        posterHashId: posterHashedId,
      }),
      async (pollingKey: string) => {
        return (await window.PMW.readLocal('posterbuilder/checkMultipageGenerationStatus', {
          pollingKey,
        })) as Promise<MultipagePreviewPollingResponse>;
      },
      LONG_PREVIEW_POLLING_TIMEOUT
    ).then((response: MultipagePreviewPollingResponse['data']) => {
      if (response?.previewUrl) {
        resolve(response.previewUrl);
      } else {
        reject(new Error('Preview generation timed out'));
      }
    }, reject);
  });
};

export const getPosterThumbnailPreviewURL = async (posterHashedId: PosterHashedId, pageHashedId?: PageHashedId): Promise<string> => {
  if (!posterHashedId) {
    return '';
  }

  return (await window.PMW.readLocal('poster/getPosterThumbnailPreviewUrl', {
    posterHashedId,
    pageHashedId,
  })) as string;
};

export const getPosterThumbnailImagePreviewURL = async (posterHashedId: PosterHashedId, pageHashedId?: PageHashedId): Promise<string> => {
  if (!posterHashedId) {
    return '';
  }

  return (await window.PMW.readLocal('poster/getPosterThumbnailImagePreviewUrl', {
    posterHashedId,
    pageHashedId,
  })) as string;
};

export const getPosterScreenPreviewURL = async (posterHashedId: PosterHashedId, pageHashedId?: PageHashedId): Promise<string> => {
  if (!posterHashedId) {
    return '';
  }

  return (await window.PMW.readLocal('poster/getPosterScreenPreviewUrl', {
    posterHashedId,
    pageHashedId,
  })) as string;
};

export const getPosterScreenImagePreviewURL = async (posterHashedId: PosterHashedId, pageHashedId?: PageHashedId): Promise<string> => {
  if (!posterHashedId) {
    return '';
  }

  return (await window.PMW.readLocal('poster/getPosterScreenImagePreviewUrl', {
    posterHashedId,
    pageHashedId,
  })) as string;
};

export const getPostersPageIds = async (posterHashedIds: PosterHashedId[]): Promise<Record<PosterHashedId, PageHashedId[]>> => {
  if (posterHashedIds.length === 0) {
    return {};
  }

  return (await window.PMW.readLocal('poster/getPostersPageIds', {
    posterHashedIds,
  })) as Record<PosterHashedId, PageHashedId[]>;
};

export const getPosterPageThumbnailPreviewURL = async (posterHashedId: string, pageHashedId?: string): Promise<string> => {
  if (!posterHashedId) {
    return '';
  }

  return (await window.PMW.readLocal('poster/getPosterPagePreviewUrl', {
    posterHashedId,
    pageHashedId,
  })) as string;
};
